<template>
	<div>
		<div v-if="isLoading" class="spinner-container">
			<Spinner />
		</div>
		<div v-else>
			<form class="comment-form" @submit.prevent="createCommentAsync">
				<textarea v-model="comment" required maxlength="4000" placeholder="Write a comment..."></textarea>
				<button type="submit" :disabled="!comment">Submit</button>
			</form>

			<div class="comments-list">
				<h4>Comments ({{ comments.length }})</h4>
				
				<div v-for="comment in comments" :key="comment.id" class="comment-item">
					<div class="comment-avatar">
						<span class="avatar-initials">{{ comment.initials }}</span>
					</div>
					<div class="comment-content">
						<span class="comment-user">
							{{ comment.userDisplayName || 'Unknown' }}

							<!-- only show pills for types other than loan -->
							<span v-if="comment.commentType !== 1" :class="['pill', getPillClass(comment.commentType)]">
								{{ getPillLabel(comment.commentType) }}
							</span>
						</span>
						<span class="comment-text">{{ comment.comment }}</span>
						<span class="comment-date">{{ comment.createdAtFormatted }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { defineProps, ref, onMounted } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import { Comment } from '@/models/loans';

	const props = defineProps<{
		loanId: number;
	}>();

	const isLoading = ref<boolean>(true);
	const comments = ref<Comment[]>([]);
	const comment = ref<string[] | string | number | undefined>(undefined);

	function getPillClass(type: number): string {
		switch (type) {
			case 2:
				return 'pill-green';
			default:
				return '';
		}
	}

	function getPillLabel(type: number): string {
		switch (type) {
			case 4:
				return 'Certification';
			case 2:
				return 'School Portal';
			default:
				return 'Other';
		}
	}

	async function getCommentsAsync() {
		try {
			const response = await axios(`/api/comments/loan/${props.loanId}`);

			comments.value = response.data;
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue getting the comments. Please try again later.'
			});
		}
	}

	async function createCommentAsync() {
		if (!comment.value) {
			return;
		}

		try {
			await axios.post('/api/comments/loan', {
				loanId: props.loanId,
				comment: comment.value
			});

			comment.value = undefined;

			await getCommentsAsync();
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue creating the comment. Please try again later.'
			});
		}
	}

	onMounted(async () => {
		await getCommentsAsync();

		isLoading.value = false;
	});
</script>

<style scoped>
	.spinner-container {
		display: flex;
		justify-content: center;
		margin-top: 5rem;
	}

	.comment-form {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-bottom: 12px;
	}

	label {
		font-weight: bold;
	}

	textarea {
		width: 100%;
		padding: 0.5rem 0.75rem;
		border: 1px solid #ccc;
		border-radius: 4px;
		resize: none;
	}

	button {
		padding: 8px 16px;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-family: 'Montserrat', Arial, sans-serif;
		transition: background-color 0.2s, color 0.2s;
		font-size: 14px;
		width: 98px;
	}

	button:hover {
		background-color: #333;
	}

	button:disabled {
		background-color: #ddd;
		color: #666;
		cursor: not-allowed;
		opacity: 0.7;
	}

	.comments-list {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.comment-item {
		display: flex;
		padding: 1rem;
		background-color: #f5f5f5;
		border: 1px solid #ddd;
		border-radius: 4px;
		flex-wrap: wrap;
	}

	.comment-avatar {
		align-self: center;
		min-width: 48px;
		min-height: 48px;
		background-color: black;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-right: 1rem;
		color: white;
		flex-shrink: 0;
	}

	.avatar-initials {
		font-size: 20px;
	}

	.comment-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.comment-user {
		font-weight: bold;
		font-size: 14px;
		text-wrap: nowrap;
	}

	.comment-text {
		font-size: 14px;
	}

	.comment-date {
		font-size: 12px;
		color: #888;
	}

	.pill {
		font-size: 10px;
		color: white;
		background-color: #888;
		padding: 4px 12px;
		font-weight: 600;
		border-radius: 12px;
		margin-left: 8px;
		text-wrap: nowrap;
	}

	.pill-green {
		background-color: #0ecb50;
	}

	@media (max-width: 486px) {
		.comment-item {
			flex-direction: column;
			align-items: flex-start;
		}

		.comment-avatar {
			align-self: flex-start;
			margin-bottom: 8px;
		}
	}
</style>
